import React, { useContext } from 'react';
import { useSelector } from 'react-redux';
import { Channel, Event } from 'stream-chat';

import { MESSAGES_PAGE } from 'src/constants';
import { PortalConfigContext } from 'src/context';
import { MessagesSidebarContext } from 'src/context/messagesSidebarContext';
import { usePageTitle } from 'src/hooks/usePageTitle';
import { useAppSelector } from 'src/hooks/useStore';
import { getChannelAvatarInfo } from 'src/legacy/components/Channels/getChannelAvatarInfo';
import {
  selectActiveCompaniesMap,
  selectClientsMap,
} from 'src/store/clients/types';
import { RootState } from 'src/store/reduxTypes';
import { DateUtils } from 'src/utils';
import { isDefined } from 'src/utils/common_utils';
import { startWithVowel, stripMarkdown } from 'src/utils/StringUtils';
import {
  getProfileFromUser,
  UserProfile,
  userProfileComparator,
} from 'src/utils/UserUtils';

export const useChannelSummaryData = (channel: Channel) => {
  const [channelTitle, setChannelTitle] = React.useState('');
  const [membersProfiles, setMembersProfiles] = React.useState<
    UserProfile[] | null
  >(null);
  const { visible: isSidebarVisible } = useContext(MessagesSidebarContext);
  const { pageTitle } = usePageTitle(MESSAGES_PAGE.label, 'messages');

  const userFullId = useSelector(
    (state: RootState) => state.messages.currentUserId,
  );
  const activeClients = useSelector(
    (state: RootState) => state.clients.activeClients,
  );
  const internalUsers = useSelector((state: RootState) => state.users.users);
  const clients = useAppSelector(selectClientsMap);
  const companies = useAppSelector(selectActiveCompaniesMap);
  const isClient = useSelector((state: RootState) => state.user.isClient);
  const portalConfig = React.useContext(PortalConfigContext);

  const getChannelTimeStamp = React.useCallback(() => {
    if (channel && channel.state && channel.state.last_message_at) {
      return DateUtils.humanizeDate(channel.state.last_message_at.toString());
    }
    if (channel && channel.data && channel.data.created_at) {
      return DateUtils.humanizeDate(channel.data.created_at as string);
    }
    return '';
  }, [channel]);
  /**
   * This function calculates the channel summary data: channel title & members
   * which will be used by the channel header and preview components.
   */
  const initializeChannelSummaryData = () => {
    if (!channel) return;

    const channelMembers = Object.values(channel.state.members);

    const displayedMembers = channelMembers
      .map((member) => member && member.user)
      .filter((u) => u && u.id !== userFullId);
    // we should filter member role owner also
    // because other internal members have role = owner

    const clientMembers = displayedMembers
      .filter((m) => m && m.role !== 'admin' && m.role !== 'owner')
      .sort((a, b) =>
        a && a.created_at && b && b.created_at && a.created_at > b.created_at
          ? 1
          : -1,
      );

    // formatting channel client member to user profiles
    const clientUserProfiles = clientMembers
      .map((clientMember) => {
        const fetchedClientUser = (
          !isClient ? activeClients : internalUsers
        ).find((client) => client.getstreamId === clientMember?.id);

        if (fetchedClientUser) {
          return getProfileFromUser(fetchedClientUser);
        }

        return null;
      })
      .filter(isDefined)
      .sort(userProfileComparator);

    setMembersProfiles(clientUserProfiles);

    let title = '';

    if (isClient && !isSidebarVisible) {
      title = pageTitle;
    } else if (channel?.data?.membershipType === 'company') {
      const channelCompany = companies.get(channel.data?.companyID as string);
      if (channelCompany) {
        title = channelCompany.fields?.name;
      }
    } else if (channel?.data?.membershipType === 'individual' && !isClient) {
      const { title: chTitle } = getChannelAvatarInfo(
        (channel.data?.singleClientId as string) || '',
        (channel.data?.companyID as string) || '',
        clients,
        companies,
      );
      title = chTitle;
    } else {
      const names = clientUserProfiles
        .map((profile) => {
          const name = `${profile?.firstName || ''} ${profile?.lastName || ''}`;

          if (name === '') {
            console.warn('No name set for user', profile?.id);
          }
          return name;
        })
        .filter((name) => name !== '')
        .join(', ');
      title = !isClient
        ? names
        : `${portalConfig.name}${names.length > 0 ? `, ${names}` : names}`;
    }
    setChannelTitle(title);
  };

  React.useEffect(() => {
    initializeChannelSummaryData();
  }, [channel, companies, activeClients, internalUsers]);

  const channelEventHandler = (ev: Event) => {
    if (ev.type === 'member.removed' || ev.type === 'member.added') {
      initializeChannelSummaryData();
    }
  };

  /**
   * This function calculates the last message
   * sent. If it a welcome channel and doesn't
   * include any message, it will show the welcome
   * message from settings redux store, otherwise
   * it shows the last message.
   * @returns channel last sent message
   */
  const getChannelLastMessage = React.useCallback(() => {
    const channelMessages = Object.values(channel?.state.messages);
    let text = 'Conversation created';
    if (
      channel &&
      channelMessages &&
      channel.data &&
      (!channel.data.isWelcomeChannel || channelMessages.length >= 1)
    ) {
      const lastMessage = channel.lastMessage();
      if (lastMessage) {
        let lastMessageUserName = '';
        const lastMessageUser = lastMessage.user;

        if (lastMessageUser && lastMessageUser.name) {
          lastMessageUserName =
            lastMessageUser.id === userFullId
              ? 'You:'
              : `${lastMessageUser.name.trim().split(' ')[0]}:`;
        }

        let suffix = '';

        const channelLastMessage = channel.lastMessage();
        if (
          channelLastMessage &&
          channelLastMessage.text &&
          channelLastMessage.text.length > 0
        ) {
          suffix = stripMarkdown(channelLastMessage.text);
        } else if (channelLastMessage) {
          const attachmentType = channelLastMessage.attachments
            ? channelLastMessage?.attachments[0]?.type
            : 'file';
          suffix = `sent ${
            attachmentType && startWithVowel(attachmentType)
              ? ` an ${attachmentType}`
              : `a ${attachmentType}`
          }`;
        }

        text = `${lastMessageUserName} ${suffix}`;
      }
    }

    return text;
  }, [channel]);

  React.useEffect(() => {
    if (channel) {
      // here we listen for channel member.remove event
      // when a member is removed we should refresh header title
      channel.on(channelEventHandler);
    }
    return () => {
      channel.off(channelEventHandler);
    };
  }, [channel]);

  return {
    channelTitle,
    channelLastMessage: getChannelLastMessage(),
    channelTimeStamp: getChannelTimeStamp(),
    membersProfiles,
  };
};
